import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, Pencil, Trash2, Plus, Check, X } from "lucide-react";

// Rozbalovací nabídka tříd (§4.2) — nahrazuje řadu chipů i řádek akcí třídy.
export function ClassMenu({ classes, activeClass, canEdit, canDelete, onSelect, onAdd, onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // {type:'add'} | {type:'rename', id}
  const [name, setName] = useState("");

  const active = classes.find((c) => c.id === activeClass);
  const countOf = (c) => (c.groups || []).reduce((s, g) => s + (g.items || []).length, 0);

  const startAdd = () => { setMode({ type: "add" }); setName(""); };
  const startRename = (c) => { setMode({ type: "rename", id: c.id }); setName(c.name); };
  const cancel = () => { setMode(null); setName(""); };
  const confirm = () => {
    const n = name.trim();
    if (!n) return;
    if (mode.type === "add") onAdd(n);
    else onRename(mode.id, n);
    cancel();
  };
  const key = (e) => { if (e.key === "Enter") confirm(); if (e.key === "Escape") cancel(); };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) cancel(); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="pasport-class-trigger"
          className="min-h-[38px] px-4 inline-flex items-center gap-2 border border-border rounded-full bg-card font-bold text-[14px] hover:bg-secondary transition-colors">
          <span className="truncate max-w-[160px]">{active?.name || "Bez třídy"}</span>
          {active && (
            <span className="bg-brand/15 text-brand h-5 min-w-5 px-1 rounded-full text-[12px] font-bold grid place-items-center tabular-nums">
              {countOf(active)}
            </span>
          )}
          <ChevronDown size={15} className="opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6}
        className="w-[320px] bg-card border border-border rounded-[14px] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.16)]"
        data-testid="pasport-class-menu">
        {classes.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">Zatím žádná třída.</p>}
        {classes.map((c) => (
          mode?.type === "rename" && mode.id === c.id ? (
            <div key={c.id} className="flex items-center gap-1.5 p-1">
              <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={key}
                className="h-9 flex-1" data-testid="pasport-class-rename-input" />
              <Button size="sm" onClick={confirm} className="h-9 font-bold" data-testid="pasport-class-rename-save">Uložit</Button>
            </div>
          ) : (
            <div key={c.id} role="button" tabIndex={0}
              onClick={() => { onSelect(c.id); setOpen(false); }}
              className={`flex items-center gap-2 rounded-[8px] px-2.5 py-2 cursor-pointer ${c.id === activeClass ? "bg-muted" : "hover:bg-muted/50"}`}
              data-testid={`pasport-class-item-${c.id}`}>
              <span className="font-bold text-[14px] truncate flex-1">{c.name}</span>
              <span className="text-[12px] text-muted-foreground tabular-nums">{countOf(c)}</span>
              {canEdit && (
                <button type="button" title="Přejmenovat" onClick={(e) => { e.stopPropagation(); startRename(c); }}
                  className="w-[30px] h-[30px] grid place-items-center rounded-[8px] text-muted-foreground hover:bg-card hover:text-brand"
                  data-testid={`pasport-class-rename-${c.id}`}><Pencil size={14} /></button>
              )}
              {canDelete && (
                <button type="button" title="Smazat třídu" onClick={(e) => { e.stopPropagation(); onDelete(c); setOpen(false); }}
                  className="w-[30px] h-[30px] grid place-items-center rounded-[8px] text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  data-testid={`pasport-class-del-${c.id}`}><Trash2 size={14} /></button>
              )}
            </div>
          )
        ))}
        {canEdit && (
          <>
            <div className="h-px bg-border my-1.5" />
            {mode?.type === "add" ? (
              <div className="flex items-center gap-1.5 p-1">
                <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={key}
                  placeholder="Název třídy" className="h-9 flex-1" data-testid="pasport-class-add-input" />
                <Button size="sm" onClick={confirm} className="h-9 font-bold" data-testid="pasport-class-add-save">Přidat</Button>
                <button type="button" onClick={cancel} className="w-9 h-9 grid place-items-center text-muted-foreground"><X size={16} /></button>
              </div>
            ) : (
              <button type="button" onClick={startAdd} data-testid="pasport-class-add-btn"
                className="w-full flex items-center gap-2 rounded-[8px] px-2.5 py-2 border border-dashed border-border text-[14px] font-semibold text-muted-foreground hover:bg-muted/50">
                <Plus size={15} /> Nová třída
              </button>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
